/**
 * Identita modulu v UI — tři vrstvy, ať nikdy nemusíš hádat, co koukáš na:
 *
 *   uuid            strojová identita (nikdy se nezobrazuje jako jméno)
 *   canonical_name  anglický familiar name PODLE ÚČELU (`reverse-proxy-manager`)
 *   label / i18n    presentace v jazyce uživatele
 *
 * Pravidlo zobrazení (Filip 2026-08-25): nahoře na stránce modulu smí být
 * hezké kanonické jméno. Nikdy holý slug — ten je jmenný prostor, ne název.
 */

/** Zkratky, které se v title-case nesmí rozbít na „Dns" / „Ocr" / „Llm". */
const ACRONYMS = new Set([
  'acm',
  'ai',
  'api',
  'ca',
  'dns',
  'http',
  'https',
  'ip',
  'llm',
  'mcp',
  'mqtt',
  'npm',
  'ocr',
  'pxe',
  'sso',
  'ui',
  'utm',
  'vpn',
]);

/** Slova s vlastní typografií — jinak by z nich title-case udělal „Gitlab". */
const PROPER: Record<string, string> = {
  gitlab: 'GitLab',
  openhands: 'OpenHands',
  supabase: 'Supabase',
  proxmox: 'Proxmox',
  rustdesk: 'RustDesk',
  vaultwarden: 'Vaultwarden',
  stalwart: 'Stalwart',
  n8n: 'n8n',
  loki: 'Loki',
  grafana: 'Grafana',
  keycloak: 'Keycloak',
  polydoc: 'PolyDoc',
  devstudio: 'DevStudio',
  apphost: 'AppHost',
  dbhost: 'DBHost',
  webhost: 'WebHost',
  acmbrain: 'ACMBrain',
};

/**
 * `reverse-proxy-manager` → `Reverse Proxy Manager`, `dns-names` → `DNS Names`.
 * Čistá funkce (testovaná) — žádné překlady, jen typografie kanonického jména.
 */
export function canonicalDisplayName(canonical: string | null | undefined): string {
  const raw = String(canonical ?? '').trim();
  if (!raw) return '';
  return raw
    .split('-')
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      if (PROPER[lower]) return PROPER[lower];
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

export interface ModuleIdentity {
  name: string;
  uuid?: string | null;
  canonical_name?: string | null;
  ui?: { label?: string | null } | null;
}

/**
 * Co napsat člověku jako název modulu. Pořadí je záměrné:
 *   1. překlad (`modules.<slug>.label`) — jazyk uživatele,
 *   2. `ui.label` z manifestu — dokud modul překlad nemá,
 *   3. kanonické jméno — vždy čitelné a v angličtině podle účelu,
 *   4. slug — poslední záchrana, nikdy ne první volba.
 */
export function moduleDisplayName(
  mod: ModuleIdentity,
  translate?: (key: string, fallback?: string) => string,
): string {
  const translated = translate?.(`modules.${mod.name}.label`, '')?.trim();
  if (translated) return translated;
  const label = String(mod.ui?.label ?? '').trim();
  if (label) return label;
  const canonical = canonicalDisplayName(mod.canonical_name);
  if (canonical) return canonical;
  return mod.name;
}
